import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heading } from '@clay-run/glaze/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { Warning } from '@phosphor-icons/react/dist/icons/Warning';
import { useAppBasePath } from '../../_shared/base-path';
import { pct, usd } from '../format';
import { FIXTURE_CAMPAIGNS, campaignTotals, type CampaignPerformance } from '../performance-fixtures';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { LinkedInCampaignsSection, MetaCampaignsSection } from './MetaCampaignsSection';

const STATUS_ORDER = { live: 0, paused: 1, ended: 2 } as const;

function needsAttention(c: CampaignPerformance) {
  const t = campaignTotals(c);
  return c.status !== 'ended' && (t.overTarget || t.belowFloor || c.proposal !== null);
}

export default function PerformanceListPage() {
  const base = useAppBasePath();

  const rows = useMemo(
    () =>
      FIXTURE_CAMPAIGNS.map(c => ({ campaign: c, totals: campaignTotals(c), attention: needsAttention(c) })).sort(
        (a, b) =>
          Number(b.attention) - Number(a.attention) ||
          STATUS_ORDER[a.campaign.status] - STATUS_ORDER[b.campaign.status] ||
          b.totals.spendUsd - a.totals.spendUsd,
      ),
    [],
  );

  const active = rows.filter(r => r.campaign.status !== 'ended');
  const activeSpend = active.reduce((s, r) => s + r.totals.spendUsd, 0);
  const attentionCount = rows.filter(r => r.attention).length;

  return (
    <section className="flex flex-col gap-6">
      <Heading level={1} size="lg">
        Performance
      </Heading>

      <MetaCampaignsSection />

      <LinkedInCampaignsSection />

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline gap-3">
          <Heading level={2} size="md">
            Sample campaigns
          </Heading>
          <Text size="sm" variant="tertiary">
            Fixture data showing CPL targets and self-healing · {active.length} running · {usd(activeSpend)} spent ·{' '}
            {attentionCount} need attention
          </Text>
        </div>

        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Campaign</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Platforms</TableHeader>
              <TableHeader align="right">Spend</TableHeader>
              <TableHeader align="right">Leads</TableHeader>
              <TableHeader align="right">CPL / target</TableHeader>
              <TableHeader align="right">Qualified / floor</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ campaign: c, totals: t, attention }) => (
              <TableRow key={c.id}>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <Link to={`${base}/performance/${c.id}`} className="font-medium hover:underline">
                      {c.name}
                    </Link>
                    <span className="flex items-center gap-1 text-xs text-content-tertiary">
                      {c.team}
                      {attention && (
                        <span className="flex items-center gap-1 text-content-warning">
                          · <Warning size={12} weight="bold" />
                          {c.proposal ? 'Action proposed' : c.status === 'paused' ? 'Paused by self-healing' : 'Off target'}
                        </span>
                      )}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <CampaignStatusBadge campaign={c} />
                </TableCell>
                <TableCell>{c.platforms.map(p => p.platform).join(', ')}</TableCell>
                <TableCell align="right">
                  <span className="font-mono">{usd(t.spendUsd)}</span>
                  <span className="block text-xs text-content-tertiary">of {usd(c.plannedSpendUsd)}</span>
                </TableCell>
                <TableCell align="right">
                  <span className="font-mono">{t.leads}</span>
                </TableCell>
                <TableCell align="right">
                  <span className={`font-mono ${t.overTarget ? 'font-semibold text-text-red' : ''}`}>{usd(t.cplUsd, 2)}</span>
                  <span className="block text-xs text-content-tertiary">target {usd(c.cplTargetUsd)}</span>
                </TableCell>
                <TableCell align="right">
                  <span className={`font-mono ${t.belowFloor ? 'font-semibold text-text-red' : ''}`}>{pct(t.qualifiedRate)}</span>
                  <span className="block text-xs text-content-tertiary">floor {pct(c.qualifiedRateFloor)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
